import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Divider,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  useTheme,
  Stack,
  IconButton,
} from "@mui/material";
import React from "react";
import {
  CheckCircleOutline,
  HourglassEmpty,
  CancelOutlined,
  LocalShipping,
  DoneAll,
  Inventory2,
  Close,
  Print,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const getStatusChipProps = (statusId, statusName) => {
  switch (statusId) {
    case 6:
      return {
        color: "warning",
        icon: <HourglassEmpty fontSize="small" />,
        label: statusName,
      };
    case 7:
      return {
        color: "info",
        icon: <CheckCircleOutline fontSize="small" />,
        label: statusName,
      };
    case 8:
      return {
        color: "primary",
        icon: <LocalShipping fontSize="small" />,
        label: statusName,
      };
    case 9:
    case 12:
      return {
        color: "success",
        icon: <DoneAll fontSize="small" />,
        label: statusName,
      };
    case 10:
    case 11:
      return {
        color: "error",
        icon: <CancelOutlined fontSize="small" />,
        label: statusName,
      };
    case 13:
      return {
        color: "secondary",
        icon: <CancelOutlined fontSize="small" />,
        label: statusName,
      };
    default:
      return { color: "default", label: statusName };
  }
};

const OrderDetailDialog = ({ open, onClose, order, fullScreen = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  if (!order) return null;

  const statusChip = getStatusChipProps(order.statusId, order.statusName);
  const paymentColor = order.paymentStatus?.toLowerCase().includes("đã")
    ? "success"
    : "warning";

  const handlePrintInvoice = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`HÓA ĐƠN BÁN HÀNG #${order.orderId}`, 14, 20);

    doc.setFontSize(10);
    doc.text(
      `Ngày đặt: ${new Date(order.orderDate).toLocaleString("vi-VN")}`,
      14,
      30
    );
    doc.text(`Người nhận: ${order.receiverName}`, 14, 36);
    doc.text(`Email: ${order.email}`, 14, 42);
    doc.text(`SĐT: ${order.phone}`, 14, 48);
    doc.text(`Địa chỉ: ${order.address}`, 14, 54);
    doc.text(`Ghi chú: ${order.note || "Không có"}`, 14, 60);

    autoTable(doc, {
      startY: 70,
      head: [["Tên SP", "Màu", "Size", "SL", "Đơn giá", "Thành tiền"]],
      body: order.orderDetails.map((item) => [
        item.productName,
        item.color || "-",
        item.size || "-",
        item.quantity,
        `${item.price.toLocaleString()}₫`,
        `${item.subtotal.toLocaleString()}₫`,
      ]),
    });

    const y = doc.lastAutoTable.finalY + 10;
    doc.text(`Tạm tính: ${order.subtotal.toLocaleString()}₫`, 14, y);
    doc.text(
      `Phí vận chuyển: ${order.shippingFee.toLocaleString()}₫`,
      14,
      y + 6
    );
    if (order.discountAmount > 0)
      doc.text(
        `Giảm giá: -${order.discountAmount.toLocaleString()}₫`,
        14,
        y + 12
      );
    if (order.loyaltyPointDiscount > 0)
      doc.text(
        `Điểm tích lũy: -${order.loyaltyPointDiscount.toLocaleString()}₫`,
        14,
        y + 18
      );
    doc.text(`Tổng tiền: ${order.totalPrice.toLocaleString()}₫`, 14, y + 26);

    doc.save(`HoaDon_${order.orderId}.pdf`);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="md"
      fullScreen={fullScreen}
      sx={{
        '& .MuiDialog-paper': {
          m: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          pb: 1, 
          pr: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}
      >
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
          Chi tiết đơn hàng #{order.orderId}
        </Typography>
        {isMobile && (
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1, overflow: 'auto' }}>
        <Box mb={2}>
          <Grid container spacing={1}>
            {[
              ["Người nhận", order.receiverName],
              ["Email", order.email],
              ["SĐT", order.phone],
              ["Địa chỉ", order.address],
              ["Ngày đặt", new Date(order.orderDate).toLocaleString("vi-VN")],
              ["Phương thức thanh toán", order.paymentMethod],
              [
                "Thời gian thanh toán",
                order.paymentTime
                  ? new Date(order.paymentTime).toLocaleString("vi-VN")
                  : "Chưa thanh toán",
              ],
              [
                "Trạng thái đơn hàng",
                <Chip {...statusChip} size="small" variant="outlined" />,
              ],
              [
                "Trạng thái thanh toán",
                <Chip
                  label={order.paymentStatus}
                  color={paymentColor}
                  size="small"
                  variant="outlined"
                />,
              ],
              ["Ghi chú", order.note || "Không có"],
            ].map(([label, value], idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={isMobile ? 12 : 4}>
                  <Typography 
                    color="text.secondary" 
                    fontSize={isMobile ? "0.875rem" : "13px"}
                    sx={{ mb: isMobile ? 0.5 : 0 }}
                  >
                    {label}
                  </Typography>
                </Grid>
                <Grid item xs={isMobile ? 12 : 8}>
                  <Typography 
                    fontWeight={500} 
                    fontSize={isMobile ? "0.875rem" : "13px"}
                    sx={{ mb: isMobile ? 1 : 0 }}
                  >
                    {value}
                  </Typography>
                </Grid>
                {isMobile && idx < 9 && <Grid item xs={12} sx={{ mb: 1 }} />}
              </React.Fragment>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Danh sách sản phẩm
        </Typography>
        <List dense disablePadding>
          {order.orderDetails.map((item, idx) => (
            <ListItem
              key={idx}
              disableGutters
              sx={{ 
                pl: 0, 
                alignItems: "flex-start",
                flexDirection: isMobile ? 'column' : 'row',
                mb: isMobile ? 2 : 0
              }}
            >
              <ListItemIcon sx={{ minWidth: isMobile ? 24 : 36, mt: isMobile ? 0.5 : 0 }}>
                <Inventory2 fontSize={isMobile ? "small" : "medium"} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600}>
                    {item.productName} - {item.color} {item.size}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" color="text.secondary">
                    SL: {item.quantity} × {item.price.toLocaleString()}₫ ={" "}
                    <strong>{item.subtotal.toLocaleString()}₫</strong>
                  </Typography>
                }
                sx={{ mt: isMobile ? 0.5 : 0 }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            backgroundColor: "grey.50",
            borderRadius: 1,
            px: 2,
            py: 1.5,
            mt: 2,
            boxShadow: 1,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2">Tạm tính</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right">
                {order.subtotal.toLocaleString()}₫
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="body2">Phí vận chuyển</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography align="right">
                {order.shippingFee.toLocaleString()}₫
              </Typography>
            </Grid>

            {order.discountAmount > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Giảm giá</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">
                    -{order.discountAmount.toLocaleString()}₫
                  </Typography>
                </Grid>
              </>
            )}

            {order.loyaltyPointDiscount > 0 && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Điểm tích lũy</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">
                    -{order.loyaltyPointDiscount.toLocaleString()}₫
                  </Typography>
                </Grid>
              </>
            )}

            {order.voucherCode && (
              <>
                <Grid item xs={6}>
                  <Typography variant="body2">Mã voucher</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography align="right">{order.voucherCode}</Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={6}>
              <Typography variant="subtitle1" fontWeight="bold">
                Tổng tiền
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                align="right"
                variant="subtitle1"
                fontWeight="bold"
                color="primary"
              >
                {order.totalPrice.toLocaleString()}₫
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={1}
          width="100%"
        >
          <Button
            onClick={handlePrintInvoice}
            variant="outlined"
            color="secondary"
            startIcon={<Print />}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "small"}
          >
            In hóa đơn
          </Button>
          <Button 
            onClick={onClose} 
            variant="contained" 
            color="primary"
            fullWidth={isMobile}
            size={isMobile ? "medium" : "small"}
          >
            Đóng
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default OrderDetailDialog;